import { supabase } from '@/lib/supabase';

export interface SentInvitation {
  id: string;
  paper_id: string;
  paper_title: string | null;
  invitee_email: string | null;
  status: string;
  personal_note: string | null;
  created_at: string;
  sent_at: string | null;
  last_sent_at: string | null;
  delivered_at: string | null;
  expires_at: string | null;
  resend_count: number;
  last_error: string | null;
}

export interface SentInvitationsResult {
  invitations: SentInvitation[];
  monthlyUsed: number;
  monthlyLimit: number;
}

const MONTHLY_LIMIT = 20;

/**
 * Returns invitations the current user has sent across ALL their papers.
 *
 * Routed through the `manage-review-invitation` edge function (action
 * `list_sent`) so that sensitive columns (`invitee_email`,
 * `invitee_email_normalized`) are not exposed via the public Data API. The
 * service-role context inside the edge function is the only client able to
 * read those columns directly. See migration revoking column SELECT.
 */
export async function getSentReviewInvitations(userId: string): Promise<SentInvitationsResult> {
  if (!userId) return { invitations: [], monthlyUsed: 0, monthlyLimit: MONTHLY_LIMIT };

  const { data, error } = await supabase.functions.invoke('manage-review-invitation', {
    body: { action: 'list_sent' },
  });
  if (error) throw error;
  if (data && typeof data === 'object' && 'error' in data && (data as { error?: unknown }).error) {
    throw new Error(String((data as { error: unknown }).error));
  }
  const payload = (data ?? {}) as Partial<SentInvitationsResult>;
  return {
    invitations: payload.invitations ?? [],
    monthlyUsed: payload.monthlyUsed ?? 0,
    monthlyLimit: payload.monthlyLimit ?? MONTHLY_LIMIT,
  };
}
